import { useEffect, useRef } from 'react'
import { styled } from '@mui/material/styles'
import { Avatar } from '@mui/material'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { colorPalette } from '../../style/colorPalette'
import { AiAgentChatMessage } from './hooks/useAiAgentConversation'

const HistoryArea = styled('div')({
  flexGrow: 1,
  overflowY: 'auto',
  padding: 16,
  backgroundColor: 'transparent',
})

const MessageContainer = styled('div', {
  shouldForwardProp: prop => prop !== 'isUser',
})<{ isUser: boolean }>(({ isUser }) => ({
  display: 'flex',
  justifyContent: isUser ? 'flex-end' : 'flex-start',
  alignItems: 'flex-start',
  marginBottom: 8,
}))

const MessageBubble = styled('div', {
  shouldForwardProp: prop => prop !== 'isUser' && prop !== 'isError',
})<{
  isUser: boolean
  isError?: boolean
}>(({ isUser, isError }) => ({
  backgroundColor: isError
    ? colorPalette.pink[1]
    : isUser
      ? colorPalette.skyBlue[1]
      : colorPalette.monotone[1],
  color: isError ? colorPalette.pink[7] : colorPalette.monotone[10],
  borderRadius: 12,
  padding: '8px 12px',
  maxWidth: '80%',
  marginLeft: isUser ? 0 : 8,
  marginRight: isUser ? 8 : 0,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  fontSize: 14,
}))

// マークダウンをチャットバブル内で表示するためのスタイル。
// 先頭・末尾要素の余白を詰めてバブルの padding と重複させない。
const MarkdownContent = styled('div')({
  // バブル側の pre-wrap を打ち消す（マークダウンはブロック要素で改行を表現するため）
  whiteSpace: 'normal',
  '& > *:first-of-type': {
    marginTop: 0,
  },
  '& > *:last-child': {
    marginBottom: 0,
  },
  '& p': {
    margin: '0 0 8px 0',
  },
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    margin: '12px 0 6px 0',
    fontWeight: 600,
    fontSize: 15,
  },
  '& ul, & ol': {
    margin: '0 0 8px 0',
    paddingLeft: 20,
  },
  '& li': {
    marginBottom: 2,
  },
  '& blockquote': {
    margin: '8px 0',
    padding: '0 12px',
    borderLeft: `3px solid ${colorPalette.monotone[3]}`,
    color: colorPalette.monotone[7],
  },
  '& code': {
    padding: '1px 4px',
    fontSize: '85%',
    backgroundColor: colorPalette.monotone[2],
    borderRadius: 3,
    fontFamily:
      'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
  },
  '& pre': {
    margin: '8px 0',
    padding: 12,
    backgroundColor: colorPalette.monotone[2],
    borderRadius: 6,
    overflow: 'auto',
    '& code': {
      padding: 0,
      backgroundColor: 'transparent',
    },
  },
  '& table': {
    borderCollapse: 'collapse',
    margin: '8px 0',
    '& th, & td': {
      border: `1px solid ${colorPalette.monotone[3]}`,
      padding: '4px 10px',
    },
    '& th': {
      backgroundColor: colorPalette.monotone[2],
      fontWeight: 600,
    },
  },
  '& a': {
    color: colorPalette.skyBlue[7],
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  '& img': {
    maxWidth: '100%',
    height: 'auto',
  },
})

const AiAvatar = styled(Avatar)({
  backgroundColor: colorPalette.skyBlue[7],
  width: 32,
  height: 32,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

const LoadingBubble = styled('div')({
  display: 'inline-block',
  width: '16px',
  height: '16px',
  border: `2px solid ${colorPalette.monotone[2]}`,
  borderTop: `2px solid ${colorPalette.skyBlue[7]}`,
  borderRadius: '50%',
  animation: 'spin 1s linear infinite',
  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
})

// アシスタントの応答のみマークダウンとして解釈する。
// ユーザー入力・エラーはプレーンテキスト（バブルの pre-wrap で改行を保持）のまま。
const renderMessageContent = (
  message: AiAgentChatMessage,
  isUser: boolean,
  showLoading: boolean
) => {
  if (showLoading) {
    return <LoadingBubble />
  }
  if (isUser || message.isError) {
    return message.text
  }
  return (
    <MarkdownContent>
      <ReactMarkdown linkTarget="_blank" remarkPlugins={[remarkGfm]}>
        {message.text}
      </ReactMarkdown>
    </MarkdownContent>
  )
}

type AiAgentMessageListProps = {
  messages: AiAgentChatMessage[]
  isStreaming: boolean
  className?: string
}

export const AiAgentMessageList = ({
  messages,
  isStreaming,
  className,
}: AiAgentMessageListProps) => {
  const historyRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (historyRef.current) {
      historyRef.current.scrollTop = historyRef.current.scrollHeight
    }
  }, [messages])

  return (
    <HistoryArea ref={historyRef} className={className}>
      {messages.map((message, index) => {
        const isUser = message.role === 'user'
        const showLoading =
          message.role === 'assistant' &&
          !message.complete &&
          message.text === '' &&
          isStreaming
        return (
          // 会話履歴は追記のみで並び替え・途中削除が発生しないため、index キーで安全。
          <MessageContainer key={`message-${index}`} isUser={isUser}>
            {!isUser && (
              <AiAvatar>
                <SmartToyIcon fontSize="small" />
              </AiAvatar>
            )}
            <MessageBubble isUser={isUser} isError={message.isError}>
              {renderMessageContent(message, isUser, showLoading)}
            </MessageBubble>
          </MessageContainer>
        )
      })}
    </HistoryArea>
  )
}
